import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import { createTemporaryUser, updateTemporaryUser } from "../../../../api/services/TemporaryUser/temporaryUser";
import { QK } from "../../../../base/qk";
import { CustomInput } from "../../../../components/CustomForm";
import CustomDateField from "../../../../components/FormFields/CustomDateField";
import CommonButton from "../../../../components/CustomForm/CommonButton";
import { ClientMultiSelect } from "../../../../components/Clients/ClientSelect";

const TemporaryInspectorDialog = ({ show, onHide, record }) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    user_type: "temporary_user",
    can_access_all_clients: false,
    allowed_clients: [],
    start_datetime: "",
    end_datetime: "",
    active: true,
  });

  // Set form data if editing
  useEffect(() => {
    if (record) {
      setFormData({
        name: record.user?.full_name || "",
        email: record.user?.email || "",
        phone: record.user?.phone || "",
        user_type: record.user_type || "temporary_user",
        can_access_all_clients: record.allow_all_clients || false,
        allowed_clients: record.clients || [],
        start_datetime: record.start_datetime || "",
        end_datetime: record.end_datetime || "",
        active: record.active ?? true,
      });
    } else {
      setFormData({
        name: "",
        email: "",
        phone: "",
        user_type: "temporary_user",
        can_access_all_clients: false,
        allowed_clients: [],
        start_datetime: "",
        end_datetime: "",
        active: true,
      });
    }
  }, [record, show]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleClientSelect = (selected) => {
    setFormData((prev) => ({
      ...prev,
      allowed_clients: selected,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        clients: formData.allowed_clients.map((c) => c.id),
      };
      if (record?.id) {
        await updateTemporaryUser(record.id, payload);
        enqueueSnackbar("Temporary user updated successfully!", { variant: "success" });
      } else {
        await createTemporaryUser(payload);
        enqueueSnackbar("Temporary user created successfully!", { variant: "success" });
      }
      queryClient.invalidateQueries(QK.temporaryUsers);
      onHide();
    } catch (error) {
      enqueueSnackbar("Failed to save temporary user.", { variant: "error" });
    }
  };

  return (
    <Dialog open={show} onClose={onHide} maxWidth="lg" fullWidth>
      <DialogTitle>{record ? "Edit Temporary User" : "Add Temporary User"}</DialogTitle>
      <DialogContent dividers>
        <form id="temporary-user-form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <CustomInput
                label="Full Name *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
              <CustomInput
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              <CustomInput
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.can_access_all_clients}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        can_access_all_clients: e.target.checked,
                      }))
                    }
                  />
                }
                label="Allow access to ALL clients"
              />
              {!formData.can_access_all_clients && (
                <ClientMultiSelect
                  label="Select Allowed Clients"
                  value={formData.allowed_clients}
                  onChange={handleClientSelect}
                  required
                />
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <CustomDateField
                label="Start Time *"
                name="start_datetime"
                type="datetime-local"
                onChange={handleChange}
                value={formData.start_datetime}
                required
              />
              <CustomDateField
                label="End Time (Optional)"
                name="end_datetime"
                type="datetime-local"
                onChange={handleChange}
                value={formData.end_datetime}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.active}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, active: e.target.checked }))
                    }
                  />
                }
                label="Active?"
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onHide}>Cancel</Button>
        <CommonButton type="submit" form="temporary-user-form">
          {record ? "Update" : "Create"}
        </CommonButton>
      </DialogActions>
    </Dialog>
  );
};

export default TemporaryInspectorDialog;